import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IdentityType,
  PersonalInfo,
  UpdatePasswordDto,
  UpdateProfileInfo
} from 'apps/user/types/on-boarding.type';
import { SpecialInstruction } from 'apps/user/types/user.enum.type';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { CreateUserResponseDto } from 'service/dto/user.dto';

export const usePersonalinfoUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (user: PersonalInfo): Promise<CreateUserResponseDto> => {
      return services.manageUsersService.updatePersonalInfo(user);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-user']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useIdentityVerificationUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (identityType: IdentityType): Promise<void> => {
      return services.manageUsersService.updateIdentityVerificationInfo(
        identityType
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-user']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useSpecialInstructionUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (specialInstruction: SpecialInstruction): Promise<any> => {
      return services.manageUsersService.updateSpecialInstruction(
        specialInstruction
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['special-instruction']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useProfileInfoUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (user: UpdateProfileInfo): Promise<any> => {
      return services.manageUsersService.updateProfileInfo(user);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['upate-user-profile']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdatePassword = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (updatePasswordDto: UpdatePasswordDto): Promise<any> => {
      return services.manageUsersService.updateProfilePassword(
        updatePasswordDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['upate-user-profile']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdateStep = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (currentStep: number): Promise<any> => {
      return services.manageUsersService.updateStep(currentStep);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['current-step']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGeneratePDF = () => {
  return useQuery(
    ['generate-pdf'],
    () => services.manageUsersService.generatePDF(),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useDownloadWill = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (user: any): Promise<any> => {
      return services.manageUsersService.downloadWill(user);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['download-will']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useSendWillForApproval = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (): Promise<any> => {
      return services.manageUsersService.sendWillForApproval();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['send-will-for-approval']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useSkipOnboardingStep = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (): Promise<any> => {
      return services.manageUsersService.skipOnboardingStep();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['skip-onboaridng']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
